import React, {
	useCallback,
	useMemo
} from 'react';
import { Checkbox } from 'antd';
import { useQuery } from 'react-query';
import { PortActionTypes } from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { PortPurposeItem } from '@api/models/organization';
import {
	getPortPurposes,
	updateOrganizationDetails
} from '@client/lib/api';
import showErrorNotification from '@client/utils/showErrorNotification';
import EditableCellTableRedux, {
	EditableColumns
} from '@client/components/EditableTableRedux/EditableCellTableRedux';
import styles from './PortPurposeSettings.module.css';

export type PortPurposeSetting = {
	purpose: Values<typeof PortActionTypes>;
	includeInEuComplianceCosts: boolean;
};

const formatPurpose = (value: unknown) => {
	const purpose = typeof value === 'string' ?
		value :
		(value as PortPurposeSetting | undefined)?.purpose;

	if (typeof purpose !== 'string') {
		return '';
	}

	return purpose
		.replace(/([A-Z])/g, ' $1')
		.replace(/^./, (c) => c.toUpperCase());
};

const PortPurposeSettings = () => {
	const {
		data: portPurposeList = [],
		refetch: refreshPortPurposeList,
		isLoading,
	} = useQuery(
		['portPurposes'],
		getPortPurposes,
	);

	const columns: EditableColumns<PortPurposeSetting> = [
		{
			title: 'Purpose',
			dataIndex: 'purpose',
			editable: false,
			type: 'text',
			render: formatPurpose,
		},
		{
			title: 'Qualifies for EU ETS, FuelEU & UK ETS',
			dataIndex: 'includeInEuComplianceCosts',
			editable: true,
			type: 'checkbox',
			align: 'center',
			renderInput: ({ value, onChange }) => (
				<div style={{ textAlign: 'center' }}>
					<Checkbox
						onChange={(e) => onChange(e.target.checked)}
						checked={value}
					/>
				</div>
			),
		}];

	const dataSource = useMemo(() => portPurposeList ?? [], [portPurposeList]);

	const handleChange = useCallback(async (values: PortPurposeSetting[]) => {
		try {
			await updateOrganizationDetails({ portPurposeList: values as PortPurposeItem[] });
			await refreshPortPurposeList();
		} catch (e) {
			showErrorNotification(
				'Could not update port purpose settings',
				e as Error,
			);
		}
	}, [refreshPortPurposeList]);

	return (
		<div className={styles.tableContainer}>
			<EditableCellTableRedux<PortPurposeSetting>
				columns={columns}
				dataSource={dataSource}
				onChange={handleChange}
				loading={isLoading}
				pagination={false}
			/>
		</div>
	);
};

export default PortPurposeSettings;